import {AfterViewInit, ChangeDetectorRef, Component, TemplateRef, ViewChild} from '@angular/core';
import {Button, ButtonDirective} from "primeng/button";
import {TranslatePipe} from "@ngx-translate/core";
import {DynamicDialogConfig, DynamicDialogRef} from "primeng/dynamicdialog";
import {FormBuilder, IMsfFormBuilder} from "@components/form-builder/form-builder";
import {Field, FieldService, Season, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {PassportManagerService} from '@app/pages/electronic-field-map/pages/fields/passport/passport-manager.service';
import {OldMapComponent} from '@components/old-map/old-map.component';
import {TabItem, TabsDynamic} from '@components/ui/tabs/tabs-dynamic';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {olCalculateAreaFromPolygon} from '@app/utils/polygons.utils';
import {buildSeasonUpdatePayload} from '@app/pages/electronic-field-map/pages/fields/passport/forms/modal-edit-season-map/season-update-payload.util';

declare var ol: any;

type HistoryEntityType = 'field' | 'season';

@Component({
  selector: 'app-modal-history-version',
  imports: [
    ButtonDirective,
    TranslatePipe,
    Button,
    FormBuilder,
    OldMapComponent,
    TabsDynamic,
  ],
  templateUrl: './modal-history-version.component.html',
  styleUrl: './modal-history-version.component.scss',
  standalone: true
})
export class ModalHistoryVersion implements AfterViewInit {
  @ViewChild('generalInfoTemplate') generalInfoTemplate!: TemplateRef<any>;
  @ViewChild('mapTemplate') mapTemplate!: TemplateRef<any>;

  model: Field | Season;
  formFields: IMsfFormBuilder[] = [];
  isLoading = false;
  isReadOnly: boolean = true;
  showRestoreButton: boolean = false;
  entityType: HistoryEntityType = 'field';
  activeTab = 'general';
  tabs: TabItem[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public fieldService: FieldService,
    private seasonService: SeasonService,
    public passportManagerService: PassportManagerService,
    private cdr: ChangeDetectorRef,
  ) {
    this.model = { ...(this.config.data?.model || {}) };
    this.normalizePolygonState(this.model);
    this.isReadOnly = this.config.data?.isReadOnly !== false;
    this.showRestoreButton = this.config.data?.showRestoreButton === true;
    this.entityType = this.config.data?.entityType === 'season' ? 'season' : 'field';
    this.formFields = this.config.data?.fields ?? [];
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.tabs = [
        {
          id: 'general',
          label: 'General information',
          template: this.generalInfoTemplate,
        },
        {
          id: 'map',
          label: 'Map',
          template: this.mapTemplate,
        },
      ];
      this.cdr.detectChanges();
    });
  }

  restore(): void {
    if (!this.model?.id) {
      return;
    }

    this.isLoading = true;

    if (this.entityType === 'season') {
      this.restoreSeason();
      return;
    }

    this.restoreField();
  }

  private restoreField(): void {
    const modelWithPublic = {
      ...(this.model as Field),
      polygonJson: this.historyPolygonJson,
    };

    if (modelWithPublic.isPublic === undefined || modelWithPublic.isPublic === null) {
      modelWithPublic.isPublic = false;
    }

    if (modelWithPublic.polygonJson) {
      modelWithPublic.fieldArea =
        this.calculateAreaFromPolygon(modelWithPublic.polygonJson) || modelWithPublic.fieldArea || null;
    }

    delete (modelWithPublic as {polygon?: string}).polygon;

    this.fieldService.cropMonitoringFieldUpdatePut(modelWithPublic).subscribe({
      next: (response) => {
        this.passportManagerService.loadField(this.model.id as string);
        this.isLoading = false;
        this.ref.close({
          restore: true,
          success: true,
          data: response || this.model,
        });
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private restoreSeason(): void {
    const seasonModel = {
      ...(this.model as Season),
      polygon: this.historyPolygonJson ?? undefined,
      polygonJson: this.historyPolygonJson ?? undefined,
    };

    if (seasonModel.polygonJson) {
      seasonModel.fieldArea =
        this.calculateAreaFromPolygon(seasonModel.polygonJson) || seasonModel.fieldArea || null;
    }

    const payload = buildSeasonUpdatePayload(seasonModel, this.historyPolygonJson);

    this.seasonService.cropMonitoringSeasonUpdatePut(payload).subscribe({
      next: (response) => {
        const fieldId = (this.model as Season).fieldId;
        if (fieldId) {
          this.passportManagerService.loadField(fieldId).subscribe();
          this.passportManagerService.loadActiveSeasons(fieldId).subscribe();
        }
        this.isLoading = false;
        this.ref.close({
          restore: true,
          success: true,
          data: response || this.model,
        });
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  cancel(): void {
    this.ref.close();
  }

  get historyPolygonJson(): string | null {
    return this.resolvePolygonValue(this.model);
  }

  private normalizePolygonState(model: any): void {
    if (!model) {
      return;
    }

    const resolvedPolygon = this.resolvePolygonValue(model);
    if (resolvedPolygon) {
      model.polygonJson = resolvedPolygon;
      model.polygon = resolvedPolygon;
    }
  }

  private resolvePolygonValue(source: any): string | null {
    if (!source) {
      return null;
    }

    if (typeof source === 'string') {
      const trimmed = source.trim();
      if (!trimmed) {
        return null;
      }

      if (trimmed.startsWith('POLYGON') || trimmed.startsWith('MULTIPOLYGON')) {
        return trimmed;
      }

      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
        try {
          return this.resolvePolygonValue(JSON.parse(trimmed));
        } catch {
          return null;
        }
      }

      return null;
    }

    if (typeof source === 'object') {
      return this.resolvePolygonValue(
        source.polygonJson ?? source.polygon ?? source.geometry ?? source.value
      );
    }

    return null;
  }

  private calculateAreaFromPolygon(polygonWkt: string): number {
    const coords = PolygonConverterUtil.polygonJsonToCoordinates(polygonWkt);
    return olCalculateAreaFromPolygon(JSON.stringify(coords), ol) as number;
  }
}
